/**
 * CoPilot Attribute Tree Builder
 *
 * Her model kendi builder fonksiyonuyla tanımlanır.
 * User modeli prefix ile hem temsilci hem ziyaretçi.owner tarafında kullanılır.
 */

// ─── User Model ─────────────────────────────────────────────
const buildUserFields = (prefix) => [
    { key: `${prefix}.name`, label: "Ad", description: "Kullanıcının adı" },
    { key: `${prefix}.role`, label: "Rol Adı", description: "Kullanıcının rol adı" },
    { key: `${prefix}.departments`, label: "Departman Adları", description: "Kullanıcının departman adları (virgülle ayrılmış)" },
];

const buildUserGroup = (prefix, label, icon) => ({
    key: prefix,
    label,
    icon: icon || "person",
    children: buildUserFields(prefix),
});

// ─── Settings Model ─────────────────────────────────────────
const buildSettingsGroup = () => ({
    key: "settings",
    label: "Ayarlar",
    icon: "business",
    children: [
        { key: "settings.organizationName", label: "Şirket Adı", description: "Organizasyon / şirket adı" },
        { key: "settings.chatbotName", label: "Chatbot Adı", description: "Chatbot asistan adı" },
    ],
});

// ─── Visitor Model ──────────────────────────────────────────
const buildVisitorGroup = () => ({
    key: "visitor",
    label: "Ziyaretçi",
    icon: "person",
    children: [
        { key: "visitor.name", label: "Ad", description: "Ziyaretçinin adı" },
        { key: "visitor.email", label: "E-posta", description: "E-posta adresi" },
        { key: "visitor.phone", label: "Telefon", description: "Telefon numarası" },
        { key: "visitor.whatsappNumber", label: "WhatsApp Numarası", description: "WhatsApp numarası" },
        { key: "visitor.language", label: "Tercih Edilen Dil", description: "Dil kodu (ör: tr, en)" },
        { key: "visitor.platform", label: "Platform", description: "Geldiği platform (web, whatsapp, instagram vb.)" },
        { key: "visitor.vipCustomer", label: "VIP Müşteri", description: "VIP müşteri durumu" },
        { key: "visitor.tags", label: "Etiket Adları", description: "Atanmış etiket adları (virgülle ayrılmış)" },
        { key: "visitor.country", label: "Ülke", description: "Ülke bilgisi" },
        { key: "visitor.city", label: "Şehir", description: "Şehir bilgisi" },
        { key: "visitor.departments", label: "Departmanlar", description: "İlişkili departman adları (virgülle ayrılmış)" },
        {
            key: "visitor.conversationUsers",
            label: "Konuşma Katılımcıları",
            description: "Son konuşmadaki temsilci adları (virgülle ayrılmış)",
        },
        {
            key: "visitor.conversationSubject",
            label: "Konuşma Konusu (Departman)",
            description: "Konuşmanın departman/konusu (kullanıcı diline göre çözümlenmiş ad)",
        },
        buildUserGroup("visitor.owner", "Sahip", "supervisor_account"),
        {
            key: "visitor.preferredAgents",
            label: "Tercih Edilen Temsilciler",
            icon: "groups",
            children: buildUserFields("visitor.preferredAgents"),
        },
        {
            key: "visitor.preferredDepartment",
            label: "Tercih Edilen Departman",
            icon: "domain",
            children: [
                { key: "visitor.preferredDepartment.name", label: "Departman Adı", description: "Tercih edilen departmanın adı" },
            ],
        },
        {
            key: "visitor.channel",
            label: "Kanal",
            icon: "forum",
            children: [
                { key: "visitor.channel.name", label: "Kanal Adı", description: "Entegrasyon kanalı adı" },
                { key: "visitor.channel.type", label: "Kanal Tipi", description: "Kanal tipi (whatsapp, instagram vb.)" },
            ],
        },
        {
            key: "visitor.metaReferrals",
            label: "Meta Reklam Referansları",
            icon: "ads_click",
            children: [
                { key: "visitor.metaReferrals.headline", label: "Başlık", description: "Son meta reklam başlığı" },
                { key: "visitor.metaReferrals.body", label: "İçerik", description: "Son meta reklam içeriği" },
                { key: "visitor.metaReferrals.sourceType", label: "Kaynak Tipi", description: "Kaynak tipi (post, ad)" },
            ],
        },
        {
            key: "visitor.messages",
            label: "Mesaj Geçmişi",
            description: "Satır satır [visitor]: / [user]: / [chatbot]: önekli metin (description dolu mesajlar)",
        },
    ],
});

// ─── Input Model ────────────────────────────────────────────
const ALL_INPUT_FIELDS = [
    { key: "message", label: "Mesaj", description: "Kullanıcının girdiği mesaj metni", icon: "chat_bubble" },
    { key: "tone", label: "Ton", description: "Uygulanacak üslup tonu (profesyonel, samimi, resmi vb.)", icon: "record_voice_over" },
    { key: "languageCode", label: "Dil Kodu", description: "Hedef dil kodu (en, de-DE, fr vb.)", icon: "translate" },
    { key: "mailDetails", label: "E-posta İçeriği", description: "Yanıtlanacak e-posta içeriği", icon: "email" },
];

const buildInputGroup = (inputKeys) => {
    const children = ALL_INPUT_FIELDS.filter((f) => inputKeys.indexOf(f.key) !== -1);
    if (children.length === 0) return null;
    return {
        key: "input",
        label: "Giriş Değerleri",
        icon: "input",
        children,
    };
};

// ─── Full tree builders ─────────────────────────────────────
const CATEGORY_BUILDERS = {
    settings: buildSettingsGroup,
    visitor: buildVisitorGroup,
    user: () => buildUserGroup("user", "Temsilci (CoPilot Kullanıcısı)", "support_agent"),
};

/**
 * Belirtilen kategori ve input key'lerine göre attribute tree oluşturur.
 * @param {string[]} categories - ["settings", "visitor", "user"]
 * @param {string[]} inputKeys  - ["message", "tone"] vb.
 * @returns {Array}
 */
export const buildAttributeTree = (categories, inputKeys) => {
    const result = [];
    for (let i = 0; i < categories.length; i++) {
        const builder = CATEGORY_BUILDERS[categories[i]];
        if (builder) result.push(builder());
    }
    const inputGroup = buildInputGroup(inputKeys || []);
    if (inputGroup) result.push(inputGroup);
    return result;
};

/**
 * Key'den kısa label üretir (ör: "visitor.owner.name" → "Sahip / Ad")
 */
export const getAttributeShortLabel = (key) => {
    const parts = [];
    _findInAllTrees(key, parts);
    if (parts.length <= 1) return parts[0] || key;
    // İlk kategoriyi atla, geri kalanı birleştir
    return parts.slice(1).join(" / ");
};

/**
 * Key'den tam label path döndürür
 */
export const getAttributeFullLabel = (key) => {
    const parts = [];
    _findInAllTrees(key, parts);
    return parts.join(" / ");
};

const _allTreesCache = {};
const _getAllTrees = () => {
    if (_allTreesCache.value) return _allTreesCache.value;
    _allTreesCache.value = buildAttributeTree(
        ["settings", "visitor", "user"],
        ["message", "tone", "languageCode", "mailDetails"]
    );
    return _allTreesCache.value;
};

const _findInAllTrees = (key, path) => {
    _findInTree(_getAllTrees(), key, path);
};

const _findInTree = (nodes, key, path) => {
    for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        if (node.key === key) {
            path.push(node.label);
            return true;
        }
        if (node.children) {
            path.push(node.label);
            if (_findInTree(node.children, key, path)) return true;
            path.pop();
        }
    }
    return false;
};
